import type {BaseOrderLine, ShoppingCartOrderLine} from '../interfaces/order.line.type';
import {isSameVarietyAndWeek} from './order.line.service';

/**
 * Returns true if the desired amount is invalid.
 */
export const isInvalidAmount = (
	amount: number,
	step: number,
	amountAvailable: number | null,
	allowOverbooking: boolean
) => {

	if (amount === undefined) {
		return true;
	}

	if (amount === null) {
		return false;
	}

	if (amount < 1) {
		return true;
	}

	if (!Number.isInteger(amount / step)) {
		return true;
	}

	if (allowOverbooking) {
		return false;
	}

	if (!amountAvailable) {
		return true;
	}

	return amount > amountAvailable;
};

/**
 * Returns true if the total amount for a variety is invalid. The total amount for a variety is invalid if the sum of
 * the amounts of all order lines of the same variety and week is higher than the available amount for that variety and
 * week.
 */
export const isInvalidTotalAmountForVariety = (
	orderLine: BaseOrderLine,
	orderLines: BaseOrderLine[],
	amountAvailable: number | null
) => {

	if (orderLine.allowOverbooking) {
		return false;
	}

	if (!amountAvailable) {
		return true;
	}

	const orderLinesOfSameVariety =
		orderLines.filter((line: BaseOrderLine) => isSameVarietyAndWeek(line, orderLine));

	if (orderLinesOfSameVariety.length === 0) {
		return true;
	}

	const sumOfAllVarieties = orderLinesOfSameVariety
		.map((line: BaseOrderLine) => line.amount)
		.reduce((prev: number, next: number) => prev + next, 0);

	return sumOfAllVarieties > amountAvailable;
};

/**
 * Returns true if at least one of the order lines has an invalid amount or if the total amount for a variety is invalid.
 */
export const isOrderLinesInvalid = (orderLines: ShoppingCartOrderLine[]) => {
	if (!orderLines) {
		return true;
	}

	return orderLines.some((orderLine) => {
		const hasInvalidAmount = isInvalidAmount(
			orderLine.amount,
			orderLine.packingUnit,
			orderLine.amountAvailable,
			orderLine.allowOverbooking
		);

		if (hasInvalidAmount) {
			return true;
		}

		return isInvalidTotalAmountForVariety(orderLine, orderLines, orderLine.amountAvailable);
	});
};
